import { useParams, Link } from 'react-router-dom'
import { ActivitySquare, ArrowLeft, Gauge, Mic2, Music2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatTile } from '@/components/dashboard/StatTile'
import { AvatarLeaderboard } from '@/components/dashboard/AvatarLeaderboard'
import { PartToWholeBreakdown } from '@/components/dashboard/PartToWholeBreakdown'
import { ThemeSpotlightCard } from '@/components/instructor/ThemeSpotlightCard'
import { useInstructorStats } from '@/hooks/useInstructorStats'
import { workoutNouns } from '@/lib/discipline'

// An instructor's totals can span multiple disciplines (many teach both
// cycling and running/walking classes), so "workouts" rather than "rides".
const { singular: WORKOUT, plural: WORKOUTS, pluralCapitalized: WORKOUTS_CAPITALIZED } = workoutNouns('all')

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function InstructorPage() {
  const { id } = useParams<{ id: string }>()
  const { data, error } = useInstructorStats(id)

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-10">
      <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" />
        Back to overview
      </Link>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="py-4 text-sm text-destructive">Couldn&apos;t load this instructor: {error}</CardContent>
        </Card>
      )}

      {data ? (
        <header className="flex items-center gap-4">
          <Avatar className="size-16 ring-1 ring-border">
            <AvatarImage src={data.instructor.image_url ?? undefined} alt="" />
            <AvatarFallback>{initials(data.instructor.name)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">{data.instructor.name}</h1>
            {data.instructor.short_bio && (
              <p className="max-w-2xl text-sm text-muted-foreground">{data.instructor.short_bio}</p>
            )}
          </div>
        </header>
      ) : (
        <div className="flex items-center gap-4">
          <Skeleton className="size-16 rounded-full" />
          <Skeleton className="h-8 w-64" />
        </div>
      )}

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {data ? (
          <>
            <StatTile label={`${WORKOUTS_CAPITALIZED} catalogued`} value={data.totals.rides} icon={ActivitySquare} />
            <StatTile label="Unique songs" value={data.totals.unique_songs} icon={Music2} />
            <StatTile label="Unique artists" value={data.totals.unique_artists} icon={Mic2} />
            <StatTile label={`Avg. songs per ${WORKOUT}`} value={data.totals.avg_songs_per_ride} icon={Gauge} />
          </>
        ) : (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[76px] rounded-xl" />)
        )}
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Redundancy score</CardTitle>
          <CardDescription>How concentrated this instructor&apos;s song rotation is</CardDescription>
        </CardHeader>
        <CardContent>
          {data ? (
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-1">
                <span className="text-3xl font-semibold tabular-nums tracking-tight">
                  {data.concentration.top_artist_share_percent}%
                </span>
                <span className="text-sm text-muted-foreground">of all song-plays are one single artist</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-3xl font-semibold tabular-nums tracking-tight">
                  {data.concentration.top_5_share_percent}%
                </span>
                <span className="text-sm text-muted-foreground">of all song-plays come from their top 5 artists</span>
              </div>
            </div>
          ) : (
            <Skeleton className="h-14 w-full" />
          )}
        </CardContent>
      </Card>

      {data?.theme_spotlight && id ? (
        <ThemeSpotlightCard instructorId={id} spotlight={data.theme_spotlight} />
      ) : (
        <Skeleton className="h-[220px] rounded-xl" />
      )}

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most-played artists</CardTitle>
            <CardDescription>Across this instructor&apos;s whole catalog</CardDescription>
          </CardHeader>
          <CardContent>
            {data ? (
              <AvatarLeaderboard
                entries={data.top_artists.map((a) => ({
                  id: a.id,
                  name: a.name,
                  image_url: a.image_url,
                  count: a.play_count,
                }))}
                valueLabel={WORKOUTS}
                size="lg"
              />
            ) : (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Genre mix</CardTitle>
            <CardDescription>What this instructor&apos;s song tags lean on</CardDescription>
          </CardHeader>
          <CardContent>
            {data ? (
              <PartToWholeBreakdown data={data.genres} unitLabel="songs" />
            ) : (
              <Skeleton className="h-[180px]" />
            )}
          </CardContent>
        </Card>
      </section>

      {data && data.themes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detected themes</CardTitle>
            <CardDescription>Parsed from class titles -- decades and genres called out by name</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-wrap gap-2">
              {data.themes.map((theme) => (
                <li key={theme.name}>
                  <Link
                    to={`/instructors/${id}/themes/${encodeURIComponent(theme.name)}`}
                    className="flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-sm hover:bg-accent"
                  >
                    <span className="font-medium">{theme.name}</span>
                    <span className="text-muted-foreground">{theme.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
